/**
 * Cambio de contexto de empresa (Mi plan ↔ equipos) — lógica pura.
 *
 * El endpoint `POST /auth/switch-tenant` ya no arrastra el permiso de licencia
 * del token vigente: lo recalcula contra la empresa destino y NIEGA el cambio si
 * no puede afirmarlo. Eso significa que el selector tiene que saber traducir tres
 * negativas distintas a algo que el usuario pueda accionar, sin jerga.
 *
 * Vive aparte de los componentes para que la barra superior y el panel del
 * Dashboard se comporten igual y para poder probarlo sin DOM.
 */

/** Rutas que dependen del contexto: no se pueden conservar al cambiar de empresa. */
const RUTAS_SEGURAS = new Set([
  '/',
  '/projects',
  '/catalogs',
  '/files',
  '/custom-dbs',
  '/account',
  '/dashboard',
  '/settings/members',
]);

/** A dónde mandar al usuario cuando cambia de contexto desde la ruta actual. */
import { nombreProductoActivo } from './brandingDelProducto.js';

export const RUTA_TRAS_CAMBIO = '/projects';

/**
 * ¿Hay que sacar al usuario de la ruta actual al cambiar de empresa?
 *
 * Un proyecto, una cotización o un catálogo del contexto anterior ya no existen
 * para la empresa nueva: quedarse ahí deja la pantalla en un 404 o, peor, en
 * datos a medio recargar. Las pantallas de listado sí sobreviven porque vuelven
 * a pedir sus datos con la sesión nueva.
 */
export function debeSalirDeLaRuta(pathname: string): boolean {
  const limpio = pathname.replace(/\/+$/, '') || '/';
  return !RUTAS_SEGURAS.has(limpio);
}

export interface SwitchTenantErrorInfo {
  /** Título corto para el aviso. */
  title: string;
  /** Qué pasó y qué puede hacer, en español y sin códigos. */
  message: string;
  /**
   * Si el usuario puede resolverlo volviendo a entrar contra Anywhere con esa
   * empresa, aquí va la ruta de login ya apuntando a ella.
   */
  reloginPath?: string;
}

/**
 * Traduce la negativa del servidor a un aviso accionable.
 *
 * - `TENANT_LICENSE_UNVERIFIED`: no se pudo AFIRMAR el derecho con datos locales
 *   (ese contexto nunca pasó por un login federado). La salida depende del tipo:
 *   una EMPRESA se arregla volviendo a entrar y seleccionándola, porque ahí
 *   Anywhere revalida la licencia. Un EQUIPO no: su slug (`tm_…`) no existe en
 *   Anywhere, así que el login lo rechazaría; su vigencia la escribe el ticket
 *   del portal, y por eso hay que volver a abrir OPUS ai desde ahí.
 * - `OPUS_SUBSCRIPTION_INACTIVE`: la suscripción venció. Volver a entrar no
 *   arregla nada; hay que renovar.
 * - `TENANT_NOT_ALLOWED`: ya no es miembro (p.ej. lo dieron de baja del equipo).
 * - `TEAM_MEMBERSHIP_REVOKED`: el padrón de eko27 dice que ya no está en el
 *   equipo. Es definitivo y el equipo desaparece del selector.
 * - `TEAM_ROSTER_UNAVAILABLE`: no se pudo confirmar el padrón (eko27 no
 *   respondió). Es temporal: se reintenta y mientras tanto se sigue trabajando
 *   en el contexto actual.
 */
export function describeSwitchTenantError(
  err: { code?: string; message?: string } | null | undefined,
  destino: { slug: string; name: string; kind?: 'team' | 'company' },
): SwitchTenantErrorInfo {
  const esEquipo = destino.kind === 'team';
  const etiqueta = esEquipo ? `el equipo ${destino.name}` : `la empresa ${destino.name}`;

  switch (err?.code) {
    case 'TENANT_LICENSE_UNVERIFIED':
      return esEquipo
        ? {
          title: 'Falta comprobar tu licencia',
          message:
            `No pudimos comprobar aquí tu licencia de ${nombreProductoActivo()} para ${etiqueta}. ` +
            `Vuelve a abrir ${nombreProductoActivo()} desde el portal eligiendo ese equipo y quedará lista.`,
        }
        : {
          title: 'Falta comprobar tu licencia',
          message:
            `No pudimos comprobar aquí tu licencia de ${nombreProductoActivo()} para ${etiqueta}. ` +
            'Vuelve a iniciar sesión eligiéndola y podrás trabajar en ella.',
          reloginPath: `/login?tenant=${encodeURIComponent(destino.slug)}&reason=relogin_tenant`,
        };
    case 'OPUS_SUBSCRIPTION_INACTIVE':
      return {
        title: 'Suscripción vencida',
        message:
          err.message?.trim()
          || `La suscripción de OPUS de ${etiqueta} está vencida. Renuévala con tu distribuidor para trabajar ahí.`,
      };
    case 'TENANT_NOT_ALLOWED':
      return {
        title: 'Sin acceso',
        message: esEquipo
          ? `Ya no formas parte de ${etiqueta}. Pide al líder del equipo que te dé de alta otra vez.`
          : `Ya no tienes acceso a ${etiqueta}.`,
      };
    case 'TEAM_MEMBERSHIP_REVOKED':
      return {
        title: 'Ya no estás en el equipo',
        message:
          err.message?.trim()
          || `El líder del equipo ${destino.name} te dio de baja. Pídele que te agregue de nuevo para volver a entrar.`,
      };
    case 'TEAM_ROSTER_UNAVAILABLE':
      return {
        title: 'No pudimos confirmar tu acceso',
        message:
          err.message?.trim()
          || `No pudimos confirmar tu acceso a ${etiqueta} en este momento. Intenta de nuevo en unos minutos; `
          + 'mientras tanto puedes seguir trabajando en el contexto actual.',
      };
    case 'TENANT_NO_PROGRAMS':
      return {
        title: `Sin ${nombreProductoActivo()} contratado`,
        message: `${capitalizar(etiqueta)} no tiene ${nombreProductoActivo()} contratado.`,
      };
    case 'DEMO_READ_ONLY':
      return {
        title: 'Sitio de demostración',
        message: 'En el sitio de demostración no se puede cambiar de empresa.',
      };
    default:
      return {
        title: 'No se pudo cambiar',
        message:
          `No pudimos abrir ${etiqueta} en este momento. Intenta de nuevo en unos segundos.`,
      };
  }
}

function capitalizar(texto: string): string {
  return texto.charAt(0).toUpperCase() + texto.slice(1);
}
